'use client';

import {
  AppRouterContext,
  type AppRouterInstance,
  type NavigateOptions,
} from 'next/dist/shared/lib/app-router-context.shared-runtime';
import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

export type NavigationRequest = {
  url: string;
  method: 'push' | 'replace';
};

/** Returns true when the interceptor took over the navigation, meaning the router must not run it. */
export type NavigationInterceptor = (request: NavigationRequest) => boolean;

export type NavigationInterceptionValue = {
  /**
   * Registers the active interceptor. Call the returned function to remove it.
   * Only one is active at a time, so the most recently registered view wins.
   */
  registerInterceptor: (interceptor: NavigationInterceptor) => () => void;
  /** The unwrapped router, so an interceptor can navigate without re-entering itself. */
  baseRouter: AppRouterInstance | null;
};

const NavigationInterceptionContext = createContext<NavigationInterceptionValue>({
  registerInterceptor: () => () => undefined,
  baseRouter: null,
});

export const useNavigationInterception = (): NavigationInterceptionValue =>
  useContext(NavigationInterceptionContext);

interface NavigationInterceptionProviderProps {
  children: React.ReactNode;
}

/**
 * Lets a view with unsaved changes intercept `router.push` / `router.replace` from anywhere in the app.
 *
 * Next.js 16 `useRouter()` returns a fresh memoized copy of the router per call site, so a hook cannot
 * reach the instance another component holds. `AppRouterContext` is the single place those copies are
 * made from, so re-providing it here covers every call site below this provider.
 */
export const NavigationInterceptionProvider: React.FC<NavigationInterceptionProviderProps> = ({ children }) => {
  const baseRouter = useContext(AppRouterContext);
  const interceptorRef = useRef<NavigationInterceptor | null>(null);

  const registerInterceptor = useCallback((interceptor: NavigationInterceptor) => {
    interceptorRef.current = interceptor;

    return (): void => {
      if (interceptorRef.current === interceptor) {
        interceptorRef.current = null;
      }
    };
  }, []);

  const interceptionValue = useMemo(
    () => ({ registerInterceptor, baseRouter }),
    [registerInterceptor, baseRouter],
  );

  const interceptingRouter = useMemo<AppRouterInstance | null>(() => {
    if (!baseRouter) {
      return null;
    }

    return {
      ...baseRouter,
      push: (href: string, options?: NavigateOptions): void => {
        if (!interceptorRef.current?.({ url: href, method: 'push' })) {
          baseRouter.push(href, options);
        }
      },
      replace: (href: string, options?: NavigateOptions): void => {
        if (!interceptorRef.current?.({ url: href, method: 'replace' })) {
          baseRouter.replace(href, options);
        }
      },
    };
  }, [baseRouter]);

  if (!interceptingRouter) {
    return (
      <NavigationInterceptionContext.Provider value={interceptionValue}>
        {children}
      </NavigationInterceptionContext.Provider>
    );
  }

  return (
    <NavigationInterceptionContext.Provider value={interceptionValue}>
      <AppRouterContext.Provider value={interceptingRouter}>
        {children}
      </AppRouterContext.Provider>
    </NavigationInterceptionContext.Provider>
  );
};
